import React from 'react';
import { withRouter } from 'react-router-dom';
import { Card, CardBody, CardFooter, CardHeader, Container } from 'reactstrap';
import './TimeOffRequestForm.scss';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { CircularProgress } from '@mui/material';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import TimeOffReasonSelect from './TimeOffRequestReasonSelect';
import TimeOffRequestDateRange from './TimeOffRequestDateRange';
import TimeOffRequestActionBar from './TimeOffRequestActionBar';
import { toasty } from '../common/forms/FormElements';
import { AppNavPaths } from '../AppNavPaths';
import TimeOffRequestStatusBar from './TimeOffRequestStatusBar';
import TimeOffNotes from './TimeOffNotes';

class TimeOffRequestForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            saving: false,
            timeOffRequest: {},
            timesheetStatuses: [],
        };

        this.onSaveClick = this.onSaveClick.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onAcceptClick = this.onAcceptClick.bind(this);
        this.onRejectClick = this.onRejectClick.bind(this);

        this.onTimeOffRequestMutated = this.onTimeOffRequestMutated.bind(this);
    }

    componentDidMount() {
        this.populateState(true);
    }

    async populateState() {
        const params = this.props.match.params;

        const { id } = params;

        const [timeOffReasons, userProfile, timeOffRequest, timesheetStatuses] =
            await Promise.all([
                util.fetch.js(ApiRoutes.timeOffReasons.all()),
                getTenantUserProfile(),
                util.fetch.get(ApiRoutes.timeOffRequests.get(id)),
                util.fetch.js(ApiRoutes.typeAheads.timesheetStatuses()),
            ]);

        const { userPermissions } = userProfile;

        const isAdmin =
            userPermissions &&
            userPermissions.includes('timeoffrequest.accept');

        const isApprover =
            userPermissions &&
            userPermissions.includes('timeoffrequest.approve');

        this.setState({
            id,
            isAdmin,
            isApprover,
            timeOffReasons,
            anyChanges: id == 0,
            timeOffRequest,
            loading: false,
            timesheetStatuses,
        });
    }

    async onSaveClick() {
        this.setState({ saving: true });

        const { timeOffRequest } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timeOffRequests.save(),
            timeOffRequest
        );

        if (saveResponse) {
            this.setState({ timeOffRequest: saveResponse, saving: false });

            toasty.success(
                'Time Off Request Saved',
                'Your time off request has been saved, but not submitted.'
            );
        } else {
            this.setState({ saving: false });
        }
    }

    async onSubmitClick() {
        this.setState({ saving: true });

        const { timeOffRequest } = this.state;
        const submitResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timeOffRequests.submit(),
            timeOffRequest
        );

        if (submitResponse) {
            this.setState({ timeOffRequest: submitResponse, saving: false });
            toasty.success(
                'Time Off Request Submitted',
                'Your time off request has been submitted.'
            );
        } else {
            this.setState({ saving: false });
        }
    }

    async onAcceptClick() {
        this.setState({ saving: true });

        const { timeOffRequest } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timeOffRequestManagement.accept(timeOffRequest.id),
            null
        );

        if (saveResponse) {
            this.setState({ timeOffRequest: saveResponse, saving: false });

            toasty.success(
                'Time Off Request Approved',
                'The time off request has been approved.'
            );
        } else {
            this.setState({ saving: false });
        }
    }

    async onRejectClick(rejectionNotes) {
        if (!rejectionNotes) {
            return;
        }

        this.setState({ saving: true });

        const { timeOffRequest } = this.state;
        const submitResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timeOffRequestManagement.reject(),
            {
                timeOffRequestId: timeOffRequest.id,
                rejectionNotes,
            }
        );

        if (submitResponse) {
            this.setState({ timeOffRequest: submitResponse, saving: false });
            toasty.success(
                'Time Off Request Rejected',
                'The time off request has been rejected.'
            );
        } else {
            this.setState({ saving: false });
        }
    }

    onRecallRequested = async () => {
        this.setState({ saving: true });

        const { timeOffRequest } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timeOffRequests.recallForEdit(timeOffRequest.id),
            null
        );

        if (saveResponse) {
            this.setState({ timeOffRequest: saveResponse, saving: false });

            toasty.success(
                'Time Off Request Recalled',
                'Your time off request has been recalled for further editing.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    onBackClick = () => {
        const { timeOffRequest } = this.state;

        if (timeOffRequest.createdByCurrentUser || !timeOffRequest.id) {
            this.props.history.push(AppNavPaths.TimeOffRequests);
        } else {
            this.props.history.push(AppNavPaths.TimesheetManagement);
        }
    };

    onTimeOffRequestMutated = (timeOffRequest) => {
        this.setState({ timeOffRequest, anyChanges: true });
    };

    getActionVerb = () => {
        const { timeOffRequest } = this.state;

        if (!timeOffRequest.id) {
            return 'Add';
        }

        if (!timeOffRequest.createdByCurrentUser) {
            return 'Manage';
        }

        return 'Edit';
    };

    render() {
        const { loading, timeOffRequest } = this.state;

        if (loading) {
            return (
                <CircularProgress color="secondary" variant="indeterminate" />
            );
        }

        const actionVerb = this.getActionVerb();

        return (
            <Container
                className="time-off-request-container p-3 m-3 ml-auto mr-auto"
                fluid={true}
            >
                <Card>
                    <CardHeader className="h3">
                        {actionVerb} Time Off Request
                        {!timeOffRequest.createdByCurrentUser && (
                            <> - {timeOffRequest.employeeName}</>
                        )}
                    </CardHeader>

                    <CardBody className="mt-2 ml-2">
                        <TimeOffRequestStatusBar
                            {...this.state}
                            onRecallRequested={this.onRecallRequested}
                        />

                        <div className="d-flex">
                            <TimeOffRequestDateRange
                                {...this.state}
                                onTimeOffRequestMutated={
                                    this.onTimeOffRequestMutated
                                }
                            />

                            <TimeOffReasonSelect
                                {...this.state}
                                onTimeOffRequestMutated={
                                    this.onTimeOffRequestMutated
                                }
                            />

                            <TimeOffNotes
                                {...this.state}
                                onTimeOffRequestMutated={
                                    this.onTimeOffRequestMutated
                                }
                            />
                        </div>
                    </CardBody>

                    <CardFooter>
                        <TimeOffRequestActionBar
                            {...this.state}
                            onSaveClick={this.onSaveClick}
                            onSubmitClick={this.onSubmitClick}
                            onBackClick={this.onBackClick}
                            onRejectClick={this.onRejectClick}
                            onAcceptClick={this.onAcceptClick}
                        />
                    </CardFooter>
                </Card>
            </Container>
        );
    }
}

export default withRouter(TimeOffRequestForm);
